import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Calendar, Shield, Key, Trash2, Eye, EyeOff, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { BayenahLogo } from '@/components/brand/BayenahLogo';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const translateAuthError = (error) => {
  const map = {
    'Invalid login credentials': 'كلمة المرور الحالية غير صحيحة',
    'New password should be different from the old password': 'كلمة المرور الجديدة يجب أن تختلف عن القديمة',
    'Password should be at least 6 characters': 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
  };
  return map[error] || error || 'حدث خطأ غير متوقع';
};

const getPasswordStrength = (password) => {
  if (!password) return { level: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 6) score++;
  if (password.length >= 10) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  if (score <= 1) return { level: 1, label: 'ضعيفة', color: 'bg-red-500' };
  if (score <= 2) return { level: 2, label: 'متوسطة', color: 'bg-yellow-500' };
  if (score <= 3) return { level: 3, label: 'جيدة', color: 'bg-blue-500' };
  return { level: 4, label: 'قوية', color: 'bg-green-500' };
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, isAuthenticated, logout, getAccessToken } = useAuth();
  const { clearAllChatData } = useChat();
  const isArabic = language === 'ar';

  // Change password
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeError, setChangeError] = useState('');
  const [changeSuccess, setChangeSuccess] = useState(false);

  // Delete account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  React.useEffect(() => {
    if (!isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  if (!user) return null;

  const getUserInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const handleSignOut = async () => {
    if (clearAllChatData) clearAllChatData();
    await logout();
    navigate('/');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangeError('');
    if (newPassword !== confirmNewPassword) {
      setChangeError(isArabic ? 'كلمتا المرور الجديدة غير متطابقتين' : 'New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setChangeError(isArabic ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
      return;
    }
    setChangeLoading(true);
    // Verify current password
    const { error: signInErr } = await supabase.auth.signInWithPassword({
      email: user.email, password: currentPassword,
    });
    if (signInErr) {
      setChangeError(isArabic ? 'كلمة المرور الحالية غير صحيحة' : 'Current password is incorrect');
      setChangeLoading(false);
      return;
    }
    const { error: updateErr } = await supabase.auth.updateUser({ password: newPassword });
    setChangeLoading(false);
    if (updateErr) {
      setChangeError(translateAuthError(updateErr.message));
      return;
    }
    setChangeSuccess(true);
    setTimeout(() => {
      setShowChangePassword(false);
      setChangeSuccess(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    }, 2000);
  };

  const handleDeleteAccount = async () => {
    const confirmText = isArabic ? 'حذف حسابي' : 'delete my account';
    if (deleteConfirmText !== confirmText) return;
    setDeleteLoading(true);
    try {
      const token = await getAccessToken();
      const API_URL = getBackendBaseUrl();
      await fetch(`${API_URL}/api/auth/account/delete`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch {
      setDeleteLoading(false);
      alert(isArabic ? 'حدث خطأ أثناء حذف الحساب' : 'Error deleting account');
    }
  };

  const strength = getPasswordStrength(newPassword);

  const t = {
    ar: {
      title: 'الملف الشخصي', back: 'رجوع', accountInfo: 'معلومات الحساب',
      name: 'الاسم', email: 'البريد الإلكتروني', provider: 'طريقة التسجيل',
      memberSince: 'عضو منذ', signOut: 'تسجيل الخروج', google: 'Google',
      emailProvider: 'البريد الإلكتروني', changePassword: 'تغيير كلمة المرور',
      deleteAccount: 'حذف الحساب', security: 'الأمان',
    },
    en: {
      title: 'Profile', back: 'Back', accountInfo: 'Account Information',
      name: 'Name', email: 'Email', provider: 'Sign-in Method',
      memberSince: 'Member Since', signOut: 'Sign Out', google: 'Google',
      emailProvider: 'Email', changePassword: 'Change Password',
      deleteAccount: 'Delete Account', security: 'Security',
    },
  };
  const txt = t[language] || t.en;

  return (
    <div className="min-h-screen bg-[#F7F7F5] text-[#1A1A1A]">
      <header className="border-b border-[#E8E8E8] px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/chat')} className="text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#E8E8E8]">
            <ArrowLeft className={cn("h-5 w-5", isArabic && "rotate-180")} />
          </Button>
          <BayenahLogo height={32} variant="dark" />
          <h1 className={cn("text-lg font-semibold", isArabic && "font-arabic")}>{txt.title}</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8" dir={isArabic ? 'rtl' : 'ltr'}>
        {/* Avatar */}
        <div className="flex flex-col items-center mb-8">
          <Avatar className="h-24 w-24 border-2 border-[#E8E8E8] mb-4">
            <AvatarImage src={user.avatar} />
            <AvatarFallback className="bg-[#1a1a1a] text-white text-2xl">
              {getUserInitials(user.name || user.email)}
            </AvatarFallback>
          </Avatar>
          <h2 className={cn("text-xl font-semibold text-[#1A1A1A]", isArabic && "font-arabic")}>
            {user.name || user.email?.split('@')[0]}
          </h2>
          {user.email && <p className="text-sm text-[#6B6B6B]">{user.email}</p>}
        </div>

        {/* Account Info Card */}
        <div className="bg-white border border-[#E8E8E8] rounded-xl p-6 mb-6">
          <h3 className={cn("text-sm font-medium text-[#6B6B6B] mb-4", isArabic && "font-arabic")}>{txt.accountInfo}</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4 py-3 border-b border-[#E8E8E8]">
              <User className="h-5 w-5 text-[#6B6B6B]" />
              <div className="flex-1">
                <p className={cn("text-xs text-[#6B6B6B]", isArabic && "font-arabic")}>{txt.name}</p>
                <p className="text-[#1A1A1A]">{user.name || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 py-3 border-b border-[#E8E8E8]">
              <Mail className="h-5 w-5 text-[#6B6B6B]" />
              <div className="flex-1">
                <p className={cn("text-xs text-[#6B6B6B]", isArabic && "font-arabic")}>{txt.email}</p>
                <p className="text-[#1A1A1A]">{user.email || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 py-3 border-b border-[#E8E8E8]">
              <Shield className="h-5 w-5 text-[#6B6B6B]" />
              <div className="flex-1">
                <p className={cn("text-xs text-[#6B6B6B]", isArabic && "font-arabic")}>{txt.provider}</p>
                <p className="text-[#1A1A1A]">{user.provider === 'google' ? txt.google : txt.emailProvider}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 py-3">
              <Calendar className="h-5 w-5 text-[#6B6B6B]" />
              <div className="flex-1">
                <p className={cn("text-xs text-[#6B6B6B]", isArabic && "font-arabic")}>{txt.memberSince}</p>
                <p className="text-[#1A1A1A]">{formatDate(user.createdAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Security Section */}
        {user.provider !== 'google' && (
          <div className="bg-white border border-[#E8E8E8] rounded-xl p-6 mb-6">
            <h3 className={cn("text-sm font-medium text-[#6B6B6B] mb-4", isArabic && "font-arabic")}>{txt.security}</h3>
            <button
              onClick={() => { setShowChangePassword(true); setChangeError(''); setChangeSuccess(false); }}
              className="flex items-center gap-4 w-full py-3 text-[#1A1A1A] hover:bg-[#F7F7F5] rounded-lg px-2 transition-colors"
              data-testid="change-password-btn"
            >
              <Key className="h-5 w-5 text-[#6B6B6B]" />
              <span>{txt.changePassword}</span>
            </button>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <Button onClick={handleSignOut} variant="outline" className="w-full h-12 border-[#E8E8E8] text-[#1A1A1A] hover:bg-[#F7F7F5]" data-testid="sign-out-btn">
            {txt.signOut}
          </Button>
          <button
            onClick={() => { setShowDeleteConfirm(true); setDeleteConfirmText(''); }}
            className="w-full h-12 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            data-testid="delete-account-btn"
          >
            {txt.deleteAccount}
          </button>
        </div>
      </main>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="max-w-md p-6 bg-white" dir="rtl">
          {changeSuccess ? (
            <div className="text-center py-6" data-testid="change-password-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-green-700 mb-2">تم بنجاح!</h3>
              <p className="text-[#6B6B6B]">تم تغيير كلمة المرور</p>
            </div>
          ) : (
            <form onSubmit={handleChangePassword}>
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-6">{isArabic ? 'تغيير كلمة المرور' : 'Change Password'}</h3>

              {changeError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 flex items-center gap-2" data-testid="change-password-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{changeError}</span>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">{isArabic ? 'كلمة المرور الحالية' : 'Current Password'}</label>
                <div className="relative">
                  <input type={showPasswords ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full p-3 border border-[#E8E8E8] rounded-lg bg-white text-[#1A1A1A] pr-4 pl-12 focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/20" required data-testid="current-password-input" />
                  <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" tabIndex={-1}>
                    {showPasswords ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">{isArabic ? 'كلمة المرور الجديدة' : 'New Password'}</label>
                <div className="relative">
                  <input type={showPasswords ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full p-3 border border-[#E8E8E8] rounded-lg bg-white text-[#1A1A1A] pr-4 pl-12 focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/20" required data-testid="new-password-input" />
                  <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" tabIndex={-1}>
                    {showPasswords ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {newPassword && (
                  <div className="mt-2">
                    <div className="flex gap-1 h-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className={`flex-1 rounded transition-colors ${i <= strength.level ? strength.color : 'bg-gray-200'}`} />
                      ))}
                    </div>
                    <p className="text-xs mt-1 text-[#6B6B6B]">{strength.label}</p>
                  </div>
                )}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">{isArabic ? 'تأكيد كلمة المرور الجديدة' : 'Confirm New Password'}</label>
                <input type={showPasswords ? 'text' : 'password'} value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="w-full p-3 border border-[#E8E8E8] rounded-lg bg-white text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/20" required data-testid="confirm-new-password-input" />
                {confirmNewPassword && newPassword !== confirmNewPassword && (
                  <p className="text-xs text-red-500 mt-1">{isArabic ? 'كلمتا المرور غير متطابقتين' : 'Passwords do not match'}</p>
                )}
              </div>

              <button type="submit" disabled={changeLoading || !currentPassword || !newPassword || newPassword !== confirmNewPassword}
                className="w-full bg-[#1a1a1a] text-white p-3 rounded-lg hover:bg-[#333] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                data-testid="save-new-password-btn">
                {changeLoading ? <><Loader2 className="w-5 h-5 animate-spin" /><span>{isArabic ? 'جاري الحفظ...' : 'Saving...'}</span></> : <span>{isArabic ? 'حفظ' : 'Save'}</span>}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md p-6 bg-white" dir="rtl">
          <h3 className="text-xl font-bold text-red-600 mb-4" data-testid="delete-account-title">
            {isArabic ? 'حذف الحساب نهائياً' : 'Delete Account Permanently'}
          </h3>
          <p className="text-[#6B6B6B] mb-4">
            {isArabic
              ? 'سيتم حذف حسابك وجميع بياناتك بشكل نهائي ولا يمكن التراجع عن هذا الإجراء.'
              : 'Your account and all data will be permanently deleted. This cannot be undone.'}
          </p>
          <p className="text-[#6B6B6B] mb-4">
            {isArabic
              ? <>للتأكيد، اكتب <span className="font-bold text-[#1A1A1A]">"حذف حسابي"</span> في الحقل أدناه:</>
              : <>Type <span className="font-bold text-[#1A1A1A]">"delete my account"</span> to confirm:</>}
          </p>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            className="w-full p-3 border border-[#E8E8E8] rounded-lg mb-4 text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-red-500/20"
            placeholder={isArabic ? 'حذف حسابي' : 'delete my account'}
            data-testid="delete-confirm-input"
          />
          <div className="flex gap-3">
            <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 p-3 border border-[#E8E8E8] rounded-lg text-[#1A1A1A] hover:bg-[#F7F7F5] transition-colors">
              {isArabic ? 'إلغاء' : 'Cancel'}
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== (isArabic ? 'حذف حسابي' : 'delete my account') || deleteLoading}
              className="flex-1 p-3 bg-red-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700 flex items-center justify-center gap-2 transition-colors"
              data-testid="confirm-delete-btn"
            >
              {deleteLoading ? <><Loader2 className="w-5 h-5 animate-spin" /><span>{isArabic ? 'جاري الحذف...' : 'Deleting...'}</span></> : <span>{isArabic ? 'حذف نهائياً' : 'Delete Permanently'}</span>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
